
import React, { useCallback, useMemo, useState } from "react";
import { FormControl, OutlinedInput, InputLabel, Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { User } from "@models";
import DandDAPI from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

// import LoadingBox from "./input/LoadingBox";

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  toggleLogin: () => void;
}

export default function Login({ toggleLogin }: Props){
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [processing, setProcessing] = useState(false);
  // const [loading, setLoading] = useState(false);
  // const [loadingMessage, setLoadingMessage] = useState("Authenticating...");
  const [registering, setRegistering] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);

  const registerMode = useCallback(() => {
    setRegistering(prev => !prev);
    setProcessing(false);
    setUsername("");
    setConfirmPassword("");
  }, []);

  const login = useCallback(async () => {
    const isFormValid = email !== "" && password !== "";
    const isRegisterValid = registering && (username === "" || password !== confirmPassword);
  
    if (!isFormValid || isRegisterValid || processing) {
      if (registering && password !== confirmPassword) {
        setMessages(prev => [...prev, "Error: Passwords do not match."]);
      }
      return;
    }
  
    setProcessing(true);
  
    // const hashedPassword = await common_util.hashPassword(password);
  
    const hashedPassword = password; // I'm not done implementing the encryption for passwords.

    if (registering) {
      const userObj = { username, email, password: hashedPassword };
      setMessages(["Registering...", `Attempt: ${JSON.stringify({ username, email })}`]);
  
      api.register(userObj)
        .then(res => {
          if (res) {
            setMessages(prev => [...prev, `${JSON.stringify(res.body)}`]);
            if (res.body && res.body.error === undefined) setRegistering(false);
            setProcessing(false);
          }
        })
        .catch(error => {
          setMessages(prev => [...prev, `Registration error: ${error.message}`]);
          setProcessing(false);
        });
    } else {
      const userObj = { email, password: hashedPassword };
      setMessages(["Logging in...", `Attempt: ${JSON.stringify({ email })}`]);
  
      api.login(userObj, true)
        .then(res => {
          setMessages(prev => [...prev, `API Response: ${JSON.stringify(res)}`]);
          if (res && res.body.error === undefined && !res.body.message && res.res instanceof User) {
            setProcessing(false);
            toggleLogin();
          } else {
            setProcessing(false);
            setMessages(prev => [...prev, "Login Failed. Check credentials."]);
          }
        })
        .catch(error => {
          setMessages(prev => [...prev, `Login error: ${error.message}`]);
          setProcessing(false);
        });
    }
  }, [username, email, password, confirmPassword, registering, processing, toggleLogin]);

  const renderHeader = useMemo(() => (
    <div key="header" className="header">
      Welcome Adventurer! Please <span className="">{registering ? "Register" : "Log In"}</span>
    </div>
  ), [registering]);

  const renderMessages = useMemo(() => (
    <Grid key="messages" container spacing={0} direction="column">
      <span className="header">Status Log:</span>
      {messages.map((message, key) => (
        <Grid key={key} className="padded">
          <span className="">[{key}]</span> {message}
        </Grid>
      ))}
    </Grid>
  ), [messages]);

  const isSubmitDisabled = processing || email === "" || password === "" ||
    (registering && (username === "" || password !== confirmPassword));

  const renderMain = useMemo(() => (
    <Grid key="main" container spacing={3} direction="column" className="">
      {registering && (
        <Grid>
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor="text_field_login_username">Username</InputLabel>
            <OutlinedInput
              id={`text_field_login_username`}
              name={`text_field_login_username`}
              type="text"
              autoComplete="Off"
              value={username}
              onChange={e => setUsername(e.target.value)}
              label="Username"
              fullWidth
            />
          </FormControl>
        </Grid>
      )}

      <Grid>
        <FormControl variant="outlined" fullWidth>
          <InputLabel htmlFor="text_field_login_email">Email</InputLabel>
          <OutlinedInput
            id={`text_field_login_email`}
            name={`text_field_login_email`}
            type="text"
            autoComplete="Off"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !isSubmitDisabled) {
                login();
              }
            }}
            label="Email"
            fullWidth
          />
        </FormControl>
      </Grid>

      <Grid>
        <FormControl variant="outlined" fullWidth>
          <InputLabel htmlFor="text_field_login_password">Password</InputLabel>
          <OutlinedInput
            id={`text_field_login_password`}
            name={`text_field_login_password`}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !isSubmitDisabled) {
                login();
              }
            }}
            label="Password"
            fullWidth
          />
        </FormControl>
      </Grid>

      {registering && (
        <Grid>
          <FormControl variant="outlined" fullWidth error={password !== confirmPassword && confirmPassword !== ""}>
            <InputLabel htmlFor="text_field_login_confirm_password">Confirm Password</InputLabel>
            <OutlinedInput
              id={`text_field_login_confirm_password`}
              name={`text_field_login_confirm_password`}
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              label="Confirm Password"
              fullWidth
            />
          </FormControl>
        </Grid>
      )}

      <Grid container spacing={2} className="mt-4" justifyContent="center">
        <Grid>
          <Button
            disabled={isSubmitDisabled}
            onClick={login}
            variant="contained"
            color="primary"
            size="large"
            className="shadow-lg hover:shadow-xl transition duration-150 ease-in-out transform hover:scale-105"
          >
            {processing ? (registering ? "Registering..." : "Logging In...") : "Submit"}
          </Button>
        </Grid>
        <Grid>
          <Button
            onClick={registerMode}
            variant="outlined"
            color="primary"
            size="large"
          >
            {registering ? "Cancel" : "Register"}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  ), [registering, username, email, password, confirmPassword, processing, login, isSubmitDisabled, registerMode]);

  if (!api.online) {
    return (
      <div className="">
        Login Unavailable when offline.
      </div>
    );
  // } else if (loading) {
  //   return (
  //     <LoadingBox
  //       message={"Authenticating..."}
  //       size={window.innerHeight * 0.5}
  //       height={window.innerHeight}
  //       width={window.innerWidth}
  //     />
  //   );
  } else {
    return (
      <div className="padded">
        {renderHeader}
        {renderMain}
        {renderMessages}
      </div>
    );
  }
}

